import { useState, useRef, useEffect, type CSSProperties } from "react";
import { Pill } from "../primitives";
import { CHANNEL_LABEL, CHANNEL_OPTIONS } from "../../lib/activity-display";
import type { LibraryNode } from "../../../lib/types";

export interface FilterState {
  /** Actor — which agent originated the activity. */
  agentId: string;
  /** Activity channel (email/call/meeting/…). */
  channel: string;
  since: string;
}

interface FilterChipsProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  agents: LibraryNode[];
}

const CHANNELS = [
  { value: "", label: "All channels" },
  ...CHANNEL_OPTIONS.map((c) => ({ value: c, label: CHANNEL_LABEL[c] })),
];

const TIME_RANGES = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "", label: "All time" },
];

function ChipDropdown({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);

  const menu: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    minWidth: 180,
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 50,
    fontFamily: "var(--font-sans)",
  };

  const menuItem: CSSProperties = {
    display: "block",
    width: "100%",
    padding: "6px 10px",
    border: "none",
    background: "transparent",
    fontSize: 13,
    textAlign: "left",
    borderRadius: "var(--border-radius-sm)",
    color: "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Pill icon="chevron-down" onClick={() => setOpen(!open)}>
        {selected?.label ?? label}
      </Pill>
      {open && (
        <div style={menu} role="menu">
          {options.map((opt) => (
            <button
              key={opt.value}
              role="menuitem"
              style={{
                ...menuItem,
                fontWeight: opt.value === value ? 500 : 400,
              }}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterChips({ filters, onChange, agents }: FilterChipsProps) {
  const agentOptions = [
    { value: "", label: "All agents" },
    ...agents.map((a) => ({ value: a.id, label: a.name })),
  ];

  return (
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <ChipDropdown
        label="All agents"
        options={agentOptions}
        value={filters.agentId}
        onChange={(v) => onChange({ ...filters, agentId: v })}
      />
      <ChipDropdown
        label="All channels"
        options={CHANNELS}
        value={filters.channel}
        onChange={(v) => onChange({ ...filters, channel: v })}
      />
      <ChipDropdown
        label="Today"
        options={TIME_RANGES}
        value={filters.since}
        onChange={(v) => onChange({ ...filters, since: v })}
      />
    </div>
  );
}
